import { EmotionCategory, getDefaultEnabledEmotions, getEmotionsByCategories, getEmotionsByTypes } from '../config/emotions';
import { AuthUser, Comment, WidgetConfig } from '../types';
import { createElement, formatDate } from '../utils';
import { MarkdownFormatter } from '../utils/markdown';
import { CommentStarRating } from './CommentStarRating';
import { MarkdownEditor } from './MarkdownEditor';

interface CommentListOptions {
  comments: Comment[];
  currentUser: AuthUser | null;
  widgetConfig: WidgetConfig;
  onReply: (parentId: string, content: string) => void;
  onReact: (commentId: string, type: string) => void;
  onEdit: (commentId: string, content: string) => void;
  onDelete: (commentId: string) => void;
  onCommentStarRate?: (commentId: string, rating: number) => void;
  onCommentStarClear?: (commentId: string) => void;
  onLoginRequired?: () => void;
  enableReactions?: boolean;
  enableCommentStarRatings?: boolean;
  allowCommentStarRatingUpdate?: boolean;
  availableEmotions?: string[];
  emotionCategories?: string[];
  theme?: 'light' | 'dark';
}

export class CommentList {
  private container: HTMLElement;
  private options: CommentListOptions;
  private markdownFormatter: MarkdownFormatter;

  constructor(options: CommentListOptions) {
    this.options = {
      enableReactions: true,
      theme: 'light',
      ...options
    };
    
    this.markdownFormatter = MarkdownFormatter.getInstance();
    this.container = this.createList();
    this.renderComments();
  }

  private createList(): HTMLElement {
    const list = createElement('div', { className: 'komentas-comment-list' });
    
    this.addStyles();
    
    return list;
  }

  private renderComments(): void {
    this.container.innerHTML = '';
    
    // Filter top-level comments (no parentId) and apply moderation visibility rules
    const topLevelComments = this.options.comments.filter(comment => 
      !comment.parentId && this.isCommentVisible(comment)
    );
    
    topLevelComments.forEach((comment) => {
      const commentElement = this.createCommentElement(comment);
      this.container.appendChild(commentElement);
    });
  }

  /**
   * Determines if a comment should be visible to the current user based on moderation status
   */
  private isCommentVisible(comment: Comment): boolean {
    // Treat undefined status as 'Published' for backward compatibility
    const status = comment.status || 'Published';
    
    // Always show published comments
    if (status === 'Published') {
      return true;
    }
    
    // Show pending comments only to their authors
    if (status === 'Pending' && this.options.currentUser?.id === comment.author.id) {
      return true;
    }
    
    // Hide all other statuses (Hidden, Deleted, Spam, Flagged) from regular users
    return false;
  }

  /**
   * Creates a status indicator for comments that need user feedback
   */
  private createStatusIndicator(comment: Comment): HTMLElement | null {
    // Only show status indicators for pending comments to their authors
    if (comment.status === 'Pending' && this.options.currentUser?.id === comment.author.id) {
      const indicator = createElement('div', { 
        className: 'komentas-status-indicator komentas-status-pending' 
      }, [
        '⏳ Your comment is awaiting approval by a moderator'
      ]);
      return indicator;
    }
    
    return null;
  }

  private createCommentElement(comment: Comment): HTMLElement {
    const commentDiv = createElement('div', { 
      className: 'komentas-comment',
      'data-comment-id': comment.id
    });

    // Comment header
    const header = this.createCommentHeader(comment);
    
    // Comment content - render Markdown to HTML safely
    const content = createElement('div', { className: 'komentas-comment-content komentas-markdown-content' });
    const renderedHtml = this.markdownFormatter.toHtml(comment.content);
    content.innerHTML = renderedHtml; // Safe because content is sanitized by MarkdownFormatter

    // Comment star rating
    const starRating = this.createCommentStarRating(comment);

    // Status indicator for pending comments (shown only to author)
    const statusIndicator = this.createStatusIndicator(comment);

    // Comment actions
    const actions = this.createCommentActions(comment);

    // Replies
    const replies = this.createRepliesSection(comment);

    commentDiv.appendChild(header);
    commentDiv.appendChild(content);
    
    if (starRating) {
      console.log(`[CommentList] Appending star rating element to comment ${comment.id}`);
      commentDiv.appendChild(starRating);
    } else {
      console.log(`[CommentList] No star rating element to append for comment ${comment.id}`);
    }
    
    if (statusIndicator) {
      commentDiv.appendChild(statusIndicator);
    }
    
    commentDiv.appendChild(actions);
    
    if (replies) {
      commentDiv.appendChild(replies);
    }

    return commentDiv;
  }

  private createCommentHeader(comment: Comment): HTMLElement {
    const header = createElement('div', { className: 'komentas-comment-header' });

    // Avatar
    const avatar = createElement('img', {
      className: 'komentas-comment-avatar',
      src: comment.author.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(comment.author.displayName)}&size=32`,
      alt: comment.author.displayName
    });

    // Author info
    const authorInfo = createElement('div', { className: 'komentas-author-info' });
    
    const authorName = createElement('span', { className: 'komentas-author-name' }, [
      comment.author.displayName
    ]);
    
    const timestamp = createElement('span', { className: 'komentas-comment-time' }, [
      formatDate(comment.createdAt)
    ]);

    // Edit indicator
    let editIndicator = null;
    if (comment.isEdited && comment.updatedAt) {
      editIndicator = createElement('span', { className: 'komentas-edit-indicator' }, [
        ` (edited ${formatDate(comment.updatedAt)})`
      ]);
    }

    authorInfo.appendChild(authorName);
    authorInfo.appendChild(document.createTextNode(' • '));
    authorInfo.appendChild(timestamp);
    
    if (editIndicator) {
      authorInfo.appendChild(editIndicator);
    }

    header.appendChild(avatar);
    header.appendChild(authorInfo);

    return header;
  }

  private createCommentStarRating(comment: Comment): HTMLElement | null {
    console.log(`[CommentStarRating] Creating star rating for comment ${comment.id}`);
    console.log(`[CommentStarRating] enableCommentStarRatings:`, this.options.enableCommentStarRatings);
    console.log(`[CommentStarRating] comment.starRating:`, comment.starRating);
    
    // Only render if feature is enabled
    if (!this.options.enableCommentStarRatings) {
      console.log(`[CommentStarRating] Feature disabled, returning null`);
      return null;
    }

    console.log(`[CommentStarRating] Creating CommentStarRating component with:`, {
      commentId: comment.id,
      average: comment.starRating?.average,
      count: comment.starRating?.count,
      userRating: comment.starRating?.userRating,
      isAuthenticated: !!this.options.currentUser
    });

    // Create star rating component
    const starRatingComponent = new CommentStarRating({
      commentId: comment.id,
      stats: {
        average: comment.starRating?.average || null,
        count: comment.starRating?.count || 0,
        distribution: comment.starRating?.distribution || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
        userRating: comment.starRating?.userRating || null
      },
      widgetConfig: this.options.widgetConfig,
      isAuthenticated: !!this.options.currentUser,
      onRatingSubmit: async (commentId: string, rating: number) => {
        console.log(`[CommentStarRating] onRatingSubmit called for comment ${commentId}, rating ${rating}`);
        if (this.options.onCommentStarRate) {
          this.options.onCommentStarRate(commentId, rating);
        }
      },
      onRatingClear: async (commentId: string) => {
        console.log(`[CommentStarRating] onRatingClear called for comment ${commentId}`);
        if (this.options.onCommentStarClear) {
          this.options.onCommentStarClear(commentId);
        }
      },
      onLoginRequired: () => {
        console.log(`[CommentStarRating] onLoginRequired called`);
        if (this.options.onLoginRequired) {
          this.options.onLoginRequired();
        }
      }
    });

    const element = starRatingComponent.getElement();
    console.log(`[CommentStarRating] Created element:`, element);
    
    return element;
  }

  private createCommentActions(comment: Comment): HTMLElement {
    const actions = createElement('div', { className: 'komentas-comment-actions' });

    // Reactions - hide if comment star ratings are enabled
    const hideReactions = this.options.enableCommentStarRatings;
    if (this.options.enableReactions && !hideReactions) {
      const reactions = this.createReactionsSection(comment);
      actions.appendChild(reactions);
    }

    // Action buttons
    const buttons = createElement('div', { className: 'komentas-action-buttons' });

    // Reply button (only for authenticated users)
    if (this.options.currentUser) {
      const replyBtn = createElement('button', {
        className: 'komentas-action-btn',
        type: 'button'
      }, ['Reply']);

      replyBtn.addEventListener('click', () => {
        this.toggleReplyForm(comment.id);
      });

      buttons.appendChild(replyBtn);
    }

    // Edit/Delete buttons (only for comment author)
    if (this.canEditComment(comment)) {
      const editBtn = createElement('button', {
        className: 'komentas-action-btn',
        type: 'button'
      }, ['Edit']);

      editBtn.addEventListener('click', () => {
        this.toggleEditMode(comment.id, comment.content);
      });

      const deleteBtn = createElement('button', {
        className: 'komentas-action-btn komentas-delete-btn',
        type: 'button'
      }, ['Delete']);

      deleteBtn.addEventListener('click', () => {
        if (confirm('Are you sure you want to delete this comment?')) {
          this.options.onDelete(comment.id);
        }
      });

      buttons.appendChild(editBtn);
      buttons.appendChild(deleteBtn);
    }

    actions.appendChild(buttons);

    return actions;
  }

  private createReactionsSection(comment: Comment): HTMLElement {
    const reactions = createElement('div', { className: 'komentas-reactions' });

    // Determine which emotions to display based on configuration
    let emotions;
    
    if (this.options.availableEmotions && this.options.availableEmotions.length > 0) {
      // Option 1: Whitelist approach - specific emotion types
      emotions = getEmotionsByTypes(this.options.availableEmotions);
    } else if (this.options.emotionCategories && this.options.emotionCategories.length > 0) {
      // Option 2: Category approach - emotions by categories
      emotions = getEmotionsByCategories(this.options.emotionCategories as EmotionCategory[]);
    } else {
      // Option 3: Default - use emotions marked as defaultEnabled (like, love, laugh, happy)
      emotions = getDefaultEnabledEmotions();
    }
    
    emotions.forEach(emotion => {
      const reaction = comment.reactions.find(r => r.type === emotion.type);
      const count = reaction ? 1 : 0; // Simplified for now
      
      // Build attributes conditionally
      const attributes: Record<string, string> = {
        className: `komentas-reaction-btn ${reaction ? 'active' : ''} ${!this.options.currentUser ? 'disabled' : ''}`,
        type: 'button',
        'data-type': emotion.type,
        title: emotion.label,
        style: 'opacity: 1 !important;'
      };
      
      // Add disabled attribute only for unauthenticated users
      if (!this.options.currentUser) {
        attributes.disabled = 'disabled';
      }
      
      const btn = createElement('button', attributes, [emotion.emoji, count > 0 ? ` ${count}` : '']);

      // Only add click handler for authenticated users
      if (this.options.currentUser) {
        btn.addEventListener('click', () => {
          this.options.onReact(comment.id, emotion.type);
        });
      }

      reactions.appendChild(btn);
    });

    return reactions;
  }

  private createRepliesSection(comment: Comment): HTMLElement | null {
    // Filter replies by both parentId and visibility rules
    const replies = this.options.comments.filter(c => 
      c.parentId === comment.id && this.isCommentVisible(c)
    );
    
    if (replies.length === 0) {
      return null;
    }

    const repliesSection = createElement('div', { className: 'komentas-replies' });
    
    replies.forEach(reply => {
      const replyElement = this.createCommentElement(reply);
      replyElement.classList.add('komentas-reply');
      repliesSection.appendChild(replyElement);
    });

    return repliesSection;
  }

  private toggleReplyForm(commentId: string): void {
    const commentElement = this.container.querySelector(`[data-comment-id="${commentId}"]`);
    if (!commentElement) return;

    // Check if reply form already exists
    const existingFormContainer = commentElement.querySelector('.komentas-reply-form-container');
    if (existingFormContainer) {
      existingFormContainer.remove();
      return;
    }

    // Create reply form
    const replyForm = this.createReplyForm(commentId);
    commentElement.appendChild(replyForm);
  }

  private createReplyForm(parentId: string): HTMLElement {
    const container = createElement('div', { className: 'komentas-reply-form-container' });
    
    const editor = new MarkdownEditor({
      onSubmit: (content: string) => {
        this.options.onReply(parentId, content);
        container.remove();
      },
      onCancel: () => {
        container.remove();
      },
      maxLength: 2000,
      theme: this.options.theme,
      placeholder: 'Write a reply...',
      submitLabel: 'Reply',
      showCancelButton: true,
      rows: 2,
      className: 'komentas-reply-form'
    });

    container.appendChild(editor.getElement());
    
    // Focus the editor after a short delay to ensure it's in the DOM
    setTimeout(() => editor.focus(), 50);

    return container;
  }

  private toggleEditMode(commentId: string, currentContent: string): void {
    const commentElement = this.container.querySelector(`[data-comment-id="${commentId}"]`);
    if (!commentElement) return;

    const contentElement = commentElement.querySelector('.komentas-comment-content') as HTMLElement;
    if (!contentElement) return;

    // Check if already in edit mode
    const existingFormContainer = commentElement.querySelector('.komentas-edit-form-container');
    if (existingFormContainer) {
      existingFormContainer.remove();
      contentElement.style.display = 'block';
      return;
    }

    // Hide original content
    contentElement.style.display = 'none';

    // Create edit form
    const editForm = this.createEditForm(commentId, currentContent);
    contentElement.parentNode!.insertBefore(editForm, contentElement.nextSibling);
  }

  private createEditForm(commentId: string, currentContent: string): HTMLElement {
    const container = createElement('div', { className: 'komentas-edit-form-container' });
    
    const editor = new MarkdownEditor({
      onSubmit: (content: string) => {
        if (content !== currentContent) {
          this.options.onEdit(commentId, content);
        }
        this.cancelEdit(commentId);
      },
      onCancel: () => {
        this.cancelEdit(commentId);
      },
      maxLength: 2000,
      theme: this.options.theme,
      placeholder: 'Edit your comment...',
      initialContent: currentContent,
      submitLabel: 'Save',
      showCancelButton: true,
      rows: 3,
      className: 'komentas-edit-form'
    });

    container.appendChild(editor.getElement());
    
    // Focus the editor after a short delay to ensure it's in the DOM
    setTimeout(() => editor.focus(), 50);

    return container;
  }

  private cancelEdit(commentId: string): void {
    const commentElement = this.container.querySelector(`[data-comment-id="${commentId}"]`);
    if (!commentElement) return;

    const editFormContainer = commentElement.querySelector('.komentas-edit-form-container');
    const contentElement = commentElement.querySelector('.komentas-comment-content') as HTMLElement;

    if (editFormContainer) {
      editFormContainer.remove();
    }
    
    if (contentElement) {
      contentElement.style.display = 'block';
    }
  }

  private canEditComment(comment: Comment): boolean {
    if (!this.options.currentUser) {
      return false;
    }

    // User can edit their own comments
    return comment.author.id === this.options.currentUser.id;
  }

  private addStyles(): void {
    const isDark = this.options.theme === 'dark';
    
    const styles = `
      .komentas-comment-list {
        margin-top: 16px;
      }

      .komentas-comment {
        border: 1px solid ${isDark ? '#444' : '#e1e5e9'};
        border-radius: 8px;
        padding: 16px;
        margin-bottom: 12px;
        background: ${isDark ? '#2a2a2a' : '#ffffff'};
      }

      .komentas-reply {
        margin-left: 32px;
        margin-top: 12px;
        margin-bottom: 0;
      }

      .komentas-comment-header {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 8px;
      }

      .komentas-comment-avatar {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: ${isDark ? '#444' : '#ddd'};
      }

      .komentas-author-info {
        flex: 1;
        font-size: 14px;
      }

      .komentas-author-name {
        font-weight: 500;
        color: ${isDark ? '#fff' : '#000'};
      }

      .komentas-comment-time {
        color: ${isDark ? '#888' : '#666'};
      }

      .komentas-edit-indicator {
        color: ${isDark ? '#888' : '#666'};
        font-style: italic;
      }

      .komentas-comment-content {
        margin-bottom: 12px;
        line-height: 1.5;
        color: ${isDark ? '#fff' : '#000'};
        white-space: pre-wrap;
        word-wrap: break-word;
        overflow-wrap: break-word;
      }

      .komentas-comment-actions {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .komentas-reactions {
        display: flex;
        gap: 4px;
      }

      .komentas-reaction-btn {
        background: none;
        border: 1px solid ${isDark ? '#555' : '#ddd'};
        border-radius: 4px;
        padding: 4px 8px;
        font-size: 12px;
        cursor: pointer;
        color: ${isDark ? '#ccc' : '#666'};
        transition: all 0.2s;
        opacity: 1 !important;
      }

      .komentas-reaction-btn:hover {
        background: ${isDark ? '#333' : '#f0f0f0'};
      }

      .komentas-reaction-btn.active {
        background: #007cba;
        color: white;
        border-color: #007cba;
      }

      .komentas-reaction-btn.disabled {
        cursor: not-allowed;
        opacity: 1 !important;
      }

      .komentas-reaction-btn:disabled {
        opacity: 1 !important;
      }

      .komentas-reaction-btn.disabled:hover {
        background: none;
      }

      .komentas-action-buttons {
        display: flex;
        gap: 8px;
      }

      .komentas-action-btn {
        background: none;
        border: none;
        color: #007cba;
        font-size: 12px;
        cursor: pointer;
        padding: 4px 8px;
        border-radius: 4px;
        transition: background-color 0.2s;
      }

      .komentas-action-btn:hover {
        background: ${isDark ? '#333' : '#f0f0f0'};
      }

      .komentas-delete-btn {
        color: #dc3545;
      }

      .komentas-reply-form,
      .komentas-edit-form {
        margin-top: 12px;
        padding: 12px;
        background: ${isDark ? '#333' : '#f8f9fa'};
        border-radius: 6px;
      }

      .komentas-reply-textarea,
      .komentas-edit-textarea {
        width: 100%;
        padding: 8px;
        border: 1px solid ${isDark ? '#555' : '#ddd'};
        border-radius: 4px;
        background: ${isDark ? '#2a2a2a' : '#fff'};
        color: ${isDark ? '#fff' : '#000'};
        font-family: inherit;
        font-size: 14px;
        resize: vertical;
        margin-bottom: 8px;
      }

      .komentas-reply-textarea:focus,
      .komentas-edit-textarea:focus {
        outline: none;
        border-color: #007cba;
        box-shadow: 0 0 0 2px rgba(0, 124, 186, 0.2);
      }

      .komentas-reply-buttons,
      .komentas-edit-buttons {
        display: flex;
        gap: 8px;
        justify-content: flex-end;
      }

      .komentas-reply-submit,
      .komentas-edit-save {
        background: #007cba;
        color: white;
        border: none;
        padding: 6px 12px;
        border-radius: 4px;
        font-size: 12px;
        cursor: pointer;
      }

      .komentas-reply-submit:hover,
      .komentas-edit-save:hover {
        background: #005a8b;
      }

      .komentas-reply-cancel,
      .komentas-edit-cancel {
        background: none;
        border: 1px solid ${isDark ? '#555' : '#ddd'};
        color: ${isDark ? '#ccc' : '#666'};
        padding: 6px 12px;
        border-radius: 4px;
        font-size: 12px;
        cursor: pointer;
      }

      .komentas-reply-cancel:hover,
      .komentas-edit-cancel:hover {
        background: ${isDark ? '#333' : '#f0f0f0'};
      }

      .komentas-replies {
        margin-top: 12px;
        border-left: 2px solid ${isDark ? '#444' : '#e1e5e9'};
        padding-left: 16px;
      }

      .komentas-status-indicator {
        margin: 8px 0;
        padding: 8px 12px;
        border-radius: 4px;
        font-size: 13px;
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .komentas-status-pending {
        background: ${isDark ? '#2d2a1f' : '#fff8e1'};
        border: 1px solid ${isDark ? '#4a4315' : '#ffc107'};
        color: ${isDark ? '#ffc107' : '#856404'};
      }

      /* Markdown content styles */
      .komentas-markdown-content p {
        margin: 0 0 8px 0;
      }

      .komentas-markdown-content p:last-child {
        margin-bottom: 0;
      }

      .komentas-markdown-content strong,
      .komentas-markdown-content b {
        font-weight: 600;
        color: ${isDark ? '#fff' : '#000'};
      }

      .komentas-markdown-content em,
      .komentas-markdown-content i {
        font-style: italic !important;
      }

      .komentas-markdown-content a {
        color: #007cba;
        text-decoration: none;
        border-bottom: 1px solid transparent;
        transition: border-color 0.2s;
      }

      .komentas-markdown-content a:hover {
        border-bottom-color: #007cba;
      }

      .komentas-markdown-content a:focus {
        outline: 2px solid #007cba;
        outline-offset: 2px;
      }

      .komentas-markdown-content ul,
      .komentas-markdown-content ol {
        margin: 8px 0;
        padding-left: 24px;
      }

      .komentas-markdown-content li {
        margin: 4px 0;
      }

      .komentas-markdown-content code {
        background: ${isDark ? '#1a1a1a' : '#f5f5f5'};
        border: 1px solid ${isDark ? '#444' : '#e1e5e9'};
        border-radius: 3px;
        padding: 2px 6px;
        font-family: 'Courier New', Courier, monospace;
        font-size: 13px;
        color: ${isDark ? '#e0e0e0' : '#c33'};
      }

      .komentas-markdown-content pre {
        background: ${isDark ? '#1a1a1a' : '#f5f5f5'};
        border: 1px solid ${isDark ? '#444' : '#e1e5e9'};
        border-radius: 4px;
        padding: 12px;
        overflow-x: auto;
        margin: 8px 0;
      }

      .komentas-markdown-content pre code {
        background: none;
        border: none;
        padding: 0;
        color: ${isDark ? '#e0e0e0' : '#333'};
      }

      .komentas-markdown-content blockquote {
        border-left: 4px solid ${isDark ? '#555' : '#ddd'};
        margin: 8px 0;
        padding: 8px 12px;
        background: ${isDark ? '#1a1a1a' : '#f9f9f9'};
        color: ${isDark ? '#ccc' : '#555'};
        font-style: italic;
      }

      .komentas-markdown-content h4,
      .komentas-markdown-content h5,
      .komentas-markdown-content h6 {
        margin: 12px 0 8px 0;
        font-weight: 600;
        color: ${isDark ? '#fff' : '#000'};
      }

      .komentas-markdown-content h4 {
        font-size: 16px;
      }

      .komentas-markdown-content h5 {
        font-size: 14px;
      }

      .komentas-markdown-content h6 {
        font-size: 13px;
      }
    `;

    // Check if styles already exist
    if (!document.getElementById('komentas-comment-list-styles')) {
      const styleSheet = createElement('style', { id: 'komentas-comment-list-styles' });
      styleSheet.textContent = styles;
      document.head.appendChild(styleSheet);
    }
  }

  public getElement(): HTMLElement {
    return this.container;
  }

  public updateComments(comments: Comment[]): void {
    this.options.comments = comments;
    this.renderComments();
  }

  public updateCurrentUser(currentUser: AuthUser | null): void {
    this.options.currentUser = currentUser;
    this.renderComments();
  }

  public addComment(comment: Comment): void {
    this.options.comments.unshift(comment);
    this.renderComments();
  }

  public removeComment(commentId: string): void {
    this.options.comments = this.options.comments.filter(c => c.id !== commentId);
    this.renderComments();
  }

  public updateComment(updatedComment: Comment): void {
    const index = this.options.comments.findIndex(c => c.id === updatedComment.id);
    if (index !== -1) {
      this.options.comments[index] = updatedComment;
      this.renderComments();
    }
  }
}